import React, { useState, useMemo } from 'react';
import { supabase } from '../supabase';
import { SecondaryBtn, PrimaryBtn, CollapsibleSection } from './systemKit';
import { acceptParseSuggestion, dismissParseFlag } from '../lib/parseGuardrails';

// ─── DataReview ─────────────────────────────────────────────────────────────
// Top-of-Price-Tracker section that surfaces parse-guardrail flags. Same
// architectural pattern as VendorWatch: rendered inside <CollapsibleSection>
// (commit 2026-04-27) so the operator sees a glanceable subhead by default
// and only expands when ready to triage. See DESIGN_SYSTEM.md §Collapsible
// content sections.
//
// Card variants:
//   high   confidence  → 3-button row: Accept fix, Edit manually, Dismiss
//   medium / low       → 2-button row: Edit manually, Dismiss
//
// Subhead language is intentionally monochromatic — Data Review is a
// "needs attention" signal, not an "urgent" signal. Color coding in the
// subhead (like Vendor Watch's red/green deltas) would over-alarm.

function fmtNumber(n) {
  if (n == null || !isFinite(n)) return '—';
  const v = Number(n);
  if (Math.abs(v) >= 1000) return v.toLocaleString('en-US', { maximumFractionDigits: 0 });
  if (Math.abs(v) >= 10)   return v.toLocaleString('en-US', { maximumFractionDigits: 1 });
  return v.toLocaleString('en-US', { maximumFractionDigits: 2 });
}

function DescribeProblem({ item }) {
  // Convert the technical detection_reason into plain language. Keeps the
  // technical reason as small italic sub-text underneath.
  const reason = item.review_reason || '';
  let plain = 'This item flagged for review.';
  if (reason.includes('total_weight_lbs')) plain = `Case weight reads ${fmtNumber(item.weight_per_case)} lbs — likely OCR error.`;
  else if (reason.includes('price_per_lb')) plain = `Price/lb reads $${fmtNumber(item.price_per_lb)}/lb — outside expected range.`;
  else if (reason.includes('pack_count')) plain = `Pack count reads ${fmtNumber(item.suggested_pack_count ?? '?')} — looks implausible.`;
  else if (reason.includes('piece_weight')) plain = `Piece weight is suspect — likely OCR error.`;
  else if (reason.includes('unit_price')) plain = `Case price reads $${fmtNumber(item.current_price)} — outside expected range.`;
  return (
    <>
      <div className="text-sm" style={{ color: 'var(--text-primary)' }}>{plain}</div>
      <div className="text-xs italic mt-0.5" style={{ color: 'var(--text-muted)' }}>
        Detected from: {reason}
      </div>
    </>
  );
}

function SuggestionInset({ item }) {
  const lines = [];
  if (item.suggested_piece_weight_oz != null) {
    lines.push({
      label: 'Pack weight',
      from: item.pack_size ? item.pack_size.split('\u00D7').slice(1).join('\u00D7').trim() : '—',
      to: item.suggested_piece_weight_oz < 16
        ? `${fmtNumber(item.suggested_piece_weight_oz)} oz`
        : `${fmtNumber(item.suggested_piece_weight_oz / 16)} lb`,
    });
  }
  if (item.suggested_total_weight_lbs != null) {
    lines.push({
      label: 'Case weight',
      from: `${fmtNumber(item.weight_per_case)} lbs`,
      to: `${fmtNumber(item.suggested_total_weight_lbs)} lbs`,
    });
  }
  if (item.suggested_price_per_lb != null) {
    lines.push({
      label: 'Price/lb',
      from: `$${fmtNumber(item.price_per_lb)}/lb`,
      to: `$${fmtNumber(item.suggested_price_per_lb)}/lb`,
    });
  }
  if (lines.length === 0) return null;
  return (
    <div
      className="rounded-md mt-3"
      style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', padding: '12px' }}
    >
      <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: 6 }}>
        Suggested fix
      </div>
      <div className="space-y-1">
        {lines.map((l, i) => (
          <div key={i} className="text-sm flex flex-wrap items-baseline gap-2" style={{ fontVariantNumeric: 'tabular-nums' }}>
            <span style={{ width: 110, color: 'var(--text-secondary)' }}>{l.label}</span>
            <span style={{ color: 'var(--text-muted)' }}>{l.from}</span>
            <span style={{ color: 'var(--text-secondary)' }}>{'\u2192'}</span>
            <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>{l.to}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ReviewCard({ item, companyId, onResolved, onEdit }) {
  const [working, setWorking] = useState(false);
  const isHigh = item.review_confidence === 'high';

  async function handleAccept() {
    if (working) return;
    setWorking(true);
    try {
      const { data: userData } = await supabase.auth.getUser();
      const email = userData?.user?.email || null;
      await acceptParseSuggestion({ companyId, foodItemId: item.id, userEmail: email });
      onResolved?.();
    } catch (e) {
      console.error('[DataReview] accept fix failed', e);
    } finally {
      setWorking(false);
    }
  }

  async function handleDismiss() {
    if (working) return;
    setWorking(true);
    try {
      const { data: userData } = await supabase.auth.getUser();
      const email = userData?.user?.email || null;
      await dismissParseFlag({ foodItemId: item.id, userEmail: email, reason: null });
      onResolved?.();
    } catch (e) {
      console.error('[DataReview] dismiss failed', e);
    } finally {
      setWorking(false);
    }
  }

  return (
    <div
      className="rounded-lg p-3"
      style={{
        background: 'var(--bg-warning-subtle)',
        border: '1px solid var(--border)',
        borderLeft: '3px solid var(--text-warning)',
      }}
    >
      <div className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
        {item.normalized_name}
      </div>
      <div className="mt-0.5">
        <DescribeProblem item={item} />
      </div>
      {isHigh && <SuggestionInset item={item} />}
      <div className="flex items-center justify-end gap-2 mt-2 flex-wrap">
        {isHigh && (
          <PrimaryBtn onClick={handleAccept} disabled={working}>
            {working ? 'Working\u2026' : 'Accept fix'}
          </PrimaryBtn>
        )}
        <SecondaryBtn onClick={() => onEdit?.(item)} disabled={working}>
          Edit manually
        </SecondaryBtn>
        <button
          onClick={handleDismiss}
          disabled={working}
          className="text-xs px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50"
          style={{ color: 'var(--text-secondary)', background: 'transparent', border: 'none', cursor: working ? 'wait' : 'pointer', fontWeight: 500 }}
          onMouseEnter={e => { e.currentTarget.style.color = 'var(--text-primary)'; e.currentTarget.style.background = 'var(--bg-hover)'; }}
          onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-secondary)'; e.currentTarget.style.background = 'transparent'; }}
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}

// Short name for subhead — same shape as VendorWatch.shortName but
// inlined here so the two components don't import each other. Keeps the
// rules consistent: strip vendor prefixes, strip parentheticals, take
// the first 3 words, title-case the result.
function shortName(name) {
  if (!name) return 'Item';
  let s = String(name)
    .replace(/\(.*?\)/g, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
  s = s.replace(/^([A-Z]{2,4}\s+){1,2}(?=[A-Z])/, '');
  return s.split(/\s+/).slice(0, 3)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

// Reason category abbreviation for the subhead. Per the spec, this stays
// monochromatic — no severity color coding in Data Review's subhead.
function abbrevReason(reason) {
  if (!reason) return 'review';
  const s = String(reason).toLowerCase();
  if (s.includes('total_weight_lbs') || s.includes('weight')) return 'weight';
  if (s.includes('price_per_lb') || s.includes('unit_price') || s.includes('price')) return 'price';
  if (s.includes('pack_count') || s.includes('piece_weight') || s.includes('pack')) return 'unit';
  if (s.includes('vendor')) return 'vendor';
  if (s.includes('extreme') || s.includes('anomaly')) return 'extreme';
  return 'review';
}

// Severity score — high-confidence flags rank above medium/low so the
// subhead surfaces "we have a confident fix waiting" first.
function reviewScore(item) {
  if (item.review_confidence === 'high') return 3;
  if (item.review_confidence === 'medium') return 2;
  return 1;
}

function buildSubhead(items) {
  if (!items || items.length === 0) return null;
  const ranked = items.slice().sort((a, b) => reviewScore(b) - reviewScore(a));
  const TOP = 3;
  const visible = ranked.slice(0, TOP);
  const overflow = ranked.length - visible.length;

  return (
    <>
      {visible.map((it, i) => (
        <React.Fragment key={it.id}>
          {i > 0 && <span style={{ color: 'var(--text-muted)', margin: '0 6px' }}>{'\u00B7'}</span>}
          <span style={{ color: 'var(--text-secondary)' }}>{shortName(it.normalized_name)}</span>
          {' '}
          <span style={{ color: 'var(--text-muted)' }}>({abbrevReason(it.review_reason)})</span>
        </React.Fragment>
      ))}
      {overflow > 0 && (
        <>
          <span style={{ color: 'var(--text-muted)', margin: '0 6px' }}>{'\u00B7'}</span>
          <span style={{ color: 'var(--text-muted)' }}>+{overflow} more</span>
        </>
      )}
    </>
  );
}

// Info-circle icon — distinguishes Data Review from Vendor Watch's
// alert-triangle, per ItemRow.js + DESIGN_SYSTEM.md §Data Review queue.
const InfoIcon = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <circle cx="12" cy="12" r="10" />
    <path d="M12 16v-4" />
    <path d="M12 8h.01" />
  </svg>
);

/**
 * DataReview — top-of-Price-Tracker review queue.
 *
 * Props:
 *   companyId
 *   items: food_items rows where needs_review = TRUE (already filtered by
 *          caller). Pass `null` (vs `[]`) to render the loading state —
 *          the section header reads "Data Review · checking…" until data
 *          resolves rather than flashing "0 active" subhead.
 *   onResolved: () => void  — refresh callback after accept/dismiss
 *   onEdit: (item) => void  — open an edit modal for manual fix
 */
export default function DataReview({ companyId, items, onResolved, onEdit }) {
  const loading = items == null;
  const n = items ? items.length : 0;
  const subhead = useMemo(() => (loading ? null : buildSubhead(items)), [loading, items]);
  const isEmpty = !loading && n === 0;
  const countLabel = n > 0 ? `${n} ${n === 1 ? 'item needs attention' : 'items need attention'}` : null;

  return (
    // data-data-review-section is the scrollIntoView target used by
    // InventoryTrends, FoodPriceTracker, and VolumeDetailDrawer.
    // CollapsibleSection is a function component (swallows data-*);
    // anchor the attribute on a wrapper div.
    <div data-data-review-section>
      <CollapsibleSection
        id="data-review"
        icon={InfoIcon}
        title="Data Review"
        count={countLabel}
        countTone="amber"
        subhead={subhead}
        loading={loading}
        empty={isEmpty ? 'Nothing to review' : false}
        defaultExpanded={false}
      >
        <div className="space-y-2">
          {(items || []).map(item => (
            <ReviewCard key={item.id} item={item} companyId={companyId} onResolved={onResolved} onEdit={onEdit} />
          ))}
        </div>
      </CollapsibleSection>
    </div>
  );
}
